import { ReactNode } from "react";
import { Flex, Text } from "styles/utility-components";

import { InlineSaveListButton } from "components/entities/save_components/lists/InlineSaveListButton";
import { SaveListDrawerProvider } from "components/entities/save_components/lists/SaveListDrawerProvider";
import { ClearSelectedButton } from "lib/core_components/ClearSelectedButton";
import { Table } from "lib/core_components/Table";
import { SpendRankingRow } from "../config";
import {
  RankingEntity,
  rankingSortFromAntSorter,
  SpendRankingSort,
  UseRankingTable,
} from "../hooks/useRankingTable";

type Props = {
  /** Which side of the transaction the chart is grouped by. */
  entity: RankingEntity;
  table: UseRankingTable;
  sort: SpendRankingSort;
  onSortChange: (sort: SpendRankingSort) => void;
  selectedRowKeys: string[];
  onSelectedRowsChange: (keys: string[]) => void;
  exportAction: ReactNode;
};

/**
 * The numbers behind a Trends chart grouped by buyers or suppliers: the same entities,
 * ranked by the measure being plotted. Ticking rows saves them, as on the buyers and
 * suppliers pages.
 */
export function SpendRankingTable({
  entity,
  table,
  sort,
  onSortChange,
  selectedRowKeys,
  onSelectedRowsChange,
  exportAction,
}: Props) {
  const hasSelection = selectedRowKeys.length > 0;

  const renderBulkSave = () => {
    if (!hasSelection) {
      return null;
    }
    // One control for both: buyers and suppliers are both list entities.
    return (
      <InlineSaveListButton
        displayType="button"
        entityType={entity === "buyers" ? "buyer" : "supplier"}
        entityIds={selectedRowKeys}
      />
    );
  };

  return (
    // The save button opens a drawer this provider owns.
    <SaveListDrawerProvider>
      <Flex column gap={16}>
        <Flex justifyContent="space-between" alignItems="center" gap={16}>
          <Text h3 color="sysTextSecondary">
            Rankings
          </Text>
          <Flex alignItems="center" gap={8}>
            {hasSelection && (
              <ClearSelectedButton
                count={selectedRowKeys.length}
                onClear={() => onSelectedRowsChange([])}
              />
            )}
            {renderBulkSave()}
            {exportAction}
          </Flex>
        </Flex>
        <Table<SpendRankingRow>
          ariaLabel="Spend ranking table"
          columns={table.columns}
          dataSource={table.data}
          pagination={{ ...table.pagination, showSizeChanger: true }}
          rowKey={table.rowKey}
          rowSelection={table.rowSelection}
          loading={table.isLoading}
          onChange={(pagination, _filters, sorter) => {
            const next = rankingSortFromAntSorter(sorter);
            if (next && (next.measure !== sort.measure || next.sortOrder !== sort.sortOrder)) {
              onSortChange(next);
              return;
            }
            table.pagination.onChange(pagination.current || 1, pagination.pageSize);
          }}
          isError={table.isError}
        />
      </Flex>
    </SaveListDrawerProvider>
  );
}
